import { DataMapper } from './transport';
import { IDropdownOption } from '../domain/entities/Config';
import { ADDRESS_TYPE, INPUT_TYPE } from '../constants/Constants';
import { COUNTRY_LABELS } from '../constants/CountryLabels';
import { IFromGetJsonAddressesSchemaResponse } from './IDataMappings';
import { IAddressAttributes } from '../domain/entities/Address';

/* eslint-disable @typescript-eslint/naming-convention */

export interface ICountryCallingCodeConfig {
  country: string;
  countryCallingCode: string;
  pattern: string;
  formatter?: string;
}

export interface IAddressDisplaySchemaProperty {
  x: number;
  y: number;
  field_separator?: string;
  mask?: string;
  asNational?: boolean;
}

interface IOneOf {
  pattern: string;
  label: string;
}

type IStaticLookupName = 'stateCityUrl' | 'stateCityLockerUrl';

export type AutoChangePhoneNumberPropertyType = {
  pattern: string;
  replacement: string;
};

export type LookupData = {
  onload?: boolean;
  targetUrl: string;
  responseKeys: {
    [fieldType: string]: keyof IAddressAttributes;
  }
};

export interface ISchemaAdditionalProperties {
  autochangePhoneNumber?: AutoChangePhoneNumberPropertyType;
  excludeCountryCode?: number;
  phoneNumberFormat?: object;
  show_flag?: number;
  autofill?: string;
  depends_on_field?: keyof IAddressAttributes;
  x: number;
  y: number;
  widget?: string;
  invalidKey?: string;
  labelKey?: string;
  input_type?: INPUT_TYPE;
  display?: IAddressDisplaySchemaProperty;
  staticLookupName?: IStaticLookupName;
  r?: string; // this is also used by JP on zipcode search, so we keep if for now
  lookupData?: LookupData; // property used for api resource of that field.
  mask?: string;
  countryCodes?: ICountryCallingCodeConfig[];
  address_auto_fill_fields?: Array<keyof IAddressAttributes>; // The ones that get auto completed
  address_suggestion_fields?: Array<keyof IAddressAttributes>; // The ones we see in the dropdown
  address_search_provider?: string; // market-specific address_search backend key (e.g. "juso" for KR), read by AddressViewController
  search_trigger_length?: number; // min keyword length before address_search fires (default 4)
  readonly_field?: number;
  prefix?: string;
  validate_existence?: number;
  options?: IDropdownOption[];
}

export interface IAddressSchemaProperty {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  type?: string;
  required?: number;
  title?: string;
  additionalProperties?: ISchemaAdditionalProperties;
  oneOf?: IOneOf[];
  default?: string;
  // Define custom validations, the 1st item in the tuple is the validation name, the 2nd is the params
  customValidations?: Array<[string, string]>;
}

export type IAddressSchemaProperties = Partial<Record<keyof IAddressAttributes, IAddressSchemaProperty>>;

export interface IAddressSchema {
  properties: IAddressSchemaProperties;
  required: Array<keyof IAddressAttributes>;
}

interface IAddressSchemaGroupDependencies {
  [key: string]: string[];
}

interface IAddressSchemaGroup {
  type?: string;
  required?: Array<keyof IAddressAttributes>;
  dependencies?: IAddressSchemaGroupDependencies;
  properties?: IAddressSchemaProperties;
}

export interface IAddressSchemaNode extends IAddressSchemaGroup {
  allOf?: IAddressSchemaGroup[];
}

interface IJsonSchemaProperties {
  addressInputSchema?: IAddressSchemaNode;
}

export interface IJsonSchema {
  $schema: string;
  title: string;
  description: string;
  type: string;
  properties: IJsonSchemaProperties;
}

export type IJsonSchemaAddressType =
  ADDRESS_TYPE.SHIPPING |
  ADDRESS_TYPE.BILLING |
  ADDRESS_TYPE.PACKSTATION;

export interface IGetAddressSchemaParams {
  brandName: string;
  locale: string;
  countryId: number;
  addressType: IJsonSchemaAddressType;
  convenienceStoreType?: string;
}

/* eslint-enable @typescript-eslint/naming-convention */

class JsonSchemaAPI {
  private dataMapper: DataMapper;

  constructor(
    dataMapper: DataMapper,
  ) {
    this.dataMapper = dataMapper;
  }

  public async getAddressSchema({
    addressType,
    brandName,
    convenienceStoreType,
    countryId,
    locale,
  }: IGetAddressSchemaParams): Promise<IFromGetJsonAddressesSchemaResponse> {
    const isConvenienceStore = !!convenienceStoreType;
    const mapAddressTypeToSchemaFolderName: Record<IJsonSchemaAddressType, string> = {
      [ADDRESS_TYPE.SHIPPING]: 'shipping',
      [ADDRESS_TYPE.BILLING]: 'billing',
      [ADDRESS_TYPE.PACKSTATION]: 'packstation',
    };
    const countryName = COUNTRY_LABELS.find(c => c.value === countryId)?.abbr?.toLowerCase() || countryId.toString();
    // Convenience store schemas live alongside the regular ones but use a "cvs-" prefix,
    // eg. "default/cvs-billing-tw.schema.json"
    const schemaName = `${isConvenienceStore ? 'cvs-' : ''}${mapAddressTypeToSchemaFolderName[addressType] || ''}`;

    const schemaFiles: string[] = [];

    // store-type specific schemas take precedence, eg. "default/cvs-billing-tw-seven-eleven.schema.json"
    if (isConvenienceStore) {
      schemaFiles.push(`${brandName}/${schemaName}-${countryName}-${convenienceStoreType}-${locale}.schema.json`);
      schemaFiles.push(`default/${schemaName}-${countryName}-${convenienceStoreType}.schema.json`);
      schemaFiles.push(`default/${schemaName}-${countryName}.schema.json`);
    } else {
      schemaFiles.push(`${brandName}/${schemaName}-${countryName}-${locale}.schema.json`); // brand-specific schema eg. "aveda/shipping-au-en-AU.json"
      schemaFiles.push(`default/${schemaName}-${countryName}.schema.json`); // country-specific schema eg. "default/shipping-au.schema.json"
      schemaFiles.push(`default/${schemaName}.schema.json`); // fallback schema eg. "default/shipping.schema.json"
    }

    for (const schemaLocation of schemaFiles) {
      try {
        const schemaModule = await import(
          /* webpackChunkName: "[request]" */
          `../application/schemas/${schemaLocation}`
        );

        const json: IJsonSchema =
          typeof schemaModule.default === 'string'
            ? await (await fetch(schemaModule.default, { cache: 'force-cache' })).json()
            : schemaModule.default as IJsonSchema;

        return this.dataMapper.mapResponse(
          'addressSchemaMapper',
          json.properties.addressInputSchema,
        ) as IFromGetJsonAddressesSchemaResponse;
      } catch (e) {
        console.warn(`Schema not found at: ${schemaLocation}. Trying next...`, e);
      }
    }
    throw new Error(`Unable to load schema. Tried:\n- ${schemaFiles.join('\n- ')}`);
  }
}

export default JsonSchemaAPI;
